import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { quoteRequestService } from '../../../services/quoteRequestService';

const QuoteRequestDetailsModal = ({ isOpen, onClose, request, onStatusChange, onRefresh }) => {
    const [selectedStatus, setSelectedStatus] = useState(request?.status);
    const [adminNotes, setAdminNotes] = useState(request?.adminNotes || '');
    const [isSaving, setIsSaving] = useState(false);

    if (!isOpen) return null;

    const handleSaveStatus = async () => {
        try {
            setIsSaving(true);
            await onStatusChange(request?.id, selectedStatus, adminNotes);
            onRefresh();
            onClose();
        } catch (error) {
            console.error('Error saving status:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const requestDetails = request?.requestDetails || {};

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm overflow-y-auto">
            <div className="bg-card rounded-xl shadow-premium-xl w-full max-w-4xl my-8">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <h2 className="text-2xl font-bold text-foreground">Détails de la Demande</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Référence : {request?.quoteNumber}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                    >
                        <Icon name="X" size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Customer Info */}
                        <div className="space-y-6">
                            <div className="bg-muted/50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <Icon name="User" size={20} />
                                    Informations Client
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-sm text-muted-foreground">Nom</label>
                                        <p className="text-foreground font-medium">{request?.customerName}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-muted-foreground">Email</label>
                                        <p className="text-foreground">{request?.customerEmail}</p>
                                        <a
                                            href={`mailto:${request?.customerEmail}`}
                                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                                        >
                                            <Icon name="Mail" size={12} />
                                            Envoyer un email
                                        </a>
                                    </div>
                                    <div>
                                        <label className="text-sm text-muted-foreground">Téléphone</label>
                                        <p className="text-foreground">{request?.customerPhone}</p>
                                        <a
                                            href={`tel:${request?.customerPhone}`}
                                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                                        >
                                            <Icon name="Phone" size={12} />
                                            Appeler
                                        </a>
                                    </div>
                                    <div>
                                        <label className="text-sm text-muted-foreground">Date de demande</label>
                                        <p className="text-foreground">
                                            {new Date(request?.createdAt)?.toLocaleDateString('fr-FR', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Status Management */}
                            <div className="bg-muted/50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <Icon name="Settings" size={20} />
                                    Gestion du Statut
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-sm text-muted-foreground block mb-2">Statut actuel</label>
                                        <select
                                            value={selectedStatus}
                                            onChange={(e) => setSelectedStatus(e?.target?.value)}
                                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                                        >
                                            <option value="pending">En attente</option>
                                            <option value="contacted">Contacté</option>
                                            <option value="accepted">Accepté</option>
                                            <option value="rejected">Refusé</option>
                                            <option value="completed">Terminé</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm text-muted-foreground block mb-2">Notes administratives</label>
                                        <textarea
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e?.target?.value)}
                                            placeholder="Ajoutez des notes internes..."
                                            rows={4}
                                            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
                                        />
                                    </div>
                                    <Button
                                        onClick={handleSaveStatus}
                                        disabled={isSaving || selectedStatus === request?.status}
                                        iconName={isSaving ? "Loader2" : "Save"}
                                        iconClassName={isSaving ? "animate-spin" : ""}
                                        iconPosition="left"
                                        className="w-full"
                                    >
                                        {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Request Details */}
                        <div className="space-y-6">
                            <div className="bg-muted/50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <Icon name="FileText" size={20} />
                                    Détails de la Demande
                                </h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-sm text-muted-foreground">Type de service</label>
                                        <p className="text-foreground font-medium">
                                            {quoteRequestService?.getTypeLabel(request?.requestType)}
                                        </p>
                                    </div>

                                    {/* Vehicle Booking Details */}
                                    {request?.requestType === 'vehicle_booking' && (
                                        <>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Véhicule</label>
                                                <p className="text-foreground font-medium">
                                                    {requestDetails?.vehicleBrand} {requestDetails?.vehicleModel}
                                                </p>
                                            </div>
                                            {requestDetails?.withDriver && (
                                                <div>
                                                    <label className="text-sm text-muted-foreground">Chauffeur</label>
                                                    <p className="text-foreground">
                                                        {requestDetails?.driverName || 'Oui'}
                                                    </p>
                                                </div>
                                            )}
                                            <div>
                                                <label className="text-sm text-muted-foreground">Période</label>
                                                <p className="text-foreground">
                                                    Du {new Date(requestDetails?.startDate)?.toLocaleDateString('fr-FR')} au{' '}
                                                    {new Date(requestDetails?.endDate)?.toLocaleDateString('fr-FR')}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    ({requestDetails?.totalDays} jour{requestDetails?.totalDays > 1 ? 's' : ''})
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Lieu de prise en charge</label>
                                                <p className="text-foreground">{requestDetails?.pickupLocation}</p>
                                            </div>
                                            {requestDetails?.dropoffLocation !== requestDetails?.pickupLocation && (
                                                <div>
                                                    <label className="text-sm text-muted-foreground">Lieu de restitution</label>
                                                    <p className="text-foreground">{requestDetails?.dropoffLocation}</p>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Property Rental Details */}
                                    {request?.requestType === 'property_rental' && (
                                        <>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Propriété</label>
                                                <p className="text-foreground font-medium">{requestDetails?.propertyTitle}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Type</label>
                                                <p className="text-foreground">{requestDetails?.propertyType}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Localisation</label>
                                                <p className="text-foreground">{requestDetails?.location}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Date d'emménagement</label>
                                                <p className="text-foreground">
                                                    {new Date(requestDetails?.moveInDate)?.toLocaleDateString('fr-FR')}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Durée du bail</label>
                                                <p className="text-foreground">{requestDetails?.leaseDuration} mois</p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground">Loyer mensuel</label>
                                                <p className="text-foreground font-medium">
                                                    {requestDetails?.monthlyRent?.toLocaleString()} CDF
                                                </p>
                                            </div>
                                        </>
                                    )}

                                    {requestDetails?.notes && (
                                        <div>
                                            <label className="text-sm text-muted-foreground">Notes du client</label>
                                            <p className="text-foreground text-sm bg-background p-3 rounded border border-border">
                                                {requestDetails?.notes}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Financial Summary */}
                            <div className="bg-muted/50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                    <Icon name="DollarSign" size={20} />
                                    Récapitulatif Financier
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Sous-total</span>
                                        <span className="text-foreground font-medium">
                                            {quoteRequestService?.formatAmount(request?.subtotal, request?.currency)}
                                        </span>
                                    </div>
                                    <div className="border-t border-border pt-2">
                                        <div className="flex justify-between">
                                            <span className="font-semibold text-foreground">Total</span>
                                            <span className="text-xl font-bold text-primary">
                                                {quoteRequestService?.formatAmount(request?.totalAmount, request?.currency)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-6 border-t border-border">
                    <Button variant="outline" onClick={onClose} className="flex-1">
                        Fermer
                    </Button>
                    <a
                        href={`mailto:${request?.customerEmail}?subject=Votre demande de devis ${request?.quoteNumber}`}
                        className="flex-1"
                    >
                        <Button iconName="Mail" iconPosition="left" className="w-full">
                            Contacter le Client
                        </Button>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default QuoteRequestDetailsModal;
